import React, { useMemo, useState } from "react";
import {
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import logo from "../../assets/logo.png";

const SideMenu = ({
  setCurrentView,
  handleClose,
  menuItems = [],
  title = "Future Trade",
  subtitle = "",
  description = "",
  defaultActiveView = "",
  currentView,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const firstView = useMemo(
    () => menuItems.find((item) => item.view)?.view || "",
    [menuItems]
  );
  const [activeView, setActiveView] = useState(defaultActiveView || firstView);
  const selectedView = currentView ?? activeView;

  const handleClick = (view, action) => {
    if (action) {
      action();
    } else if (setCurrentView) {
      setCurrentView(view);
      setActiveView(view);
    }

    if (handleClose) {
      handleClose();
    }
  };

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : "280px",
        maxWidth: isMobile ? "100%" : 320,
        boxSizing: "border-box",
        background: isMobile
          ? "linear-gradient(180deg, rgba(10,17,30,0.84) 0%, rgba(7,13,24,0.84) 100%)"
          : "linear-gradient(180deg, rgba(13,20,31,0.98) 0%, rgba(9,15,23,0.98) 100%)",
        borderRight: isMobile ? "none" : "1px solid rgba(142,169,214,0.18)",
        padding: isMobile ? "12px 12px 0" : "20px 16px 24px",
        height: isMobile ? "100%" : "100vh",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        boxShadow: isMobile ? "none" : "8px 0 34px rgba(0, 0, 0, 0.35)",
        position: "relative",
        backdropFilter: isMobile ? "blur(12px)" : "blur(8px)",
      }}
    >
      {isMobile && handleClose && (
        <IconButton
          sx={{
            alignSelf: "flex-end",
            color: "#C7D6EE",
            marginBottom: "10px",
            border: "1px solid rgba(191, 210, 243, 0.25)",
            width: 34,
            height: 34,
            "&:hover": {
              backgroundColor: "rgba(159,199,255,0.12)",
            },
          }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 1.5,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Logo"
          sx={{
            width: 78,
            height: 78,
            borderRadius: "50%",
            border: "2px solid #ff2e63",
            boxShadow: "0 10px 26px rgba(255, 46, 99, 0.24)",
            display: "block",
          }}
        />
      </Box>

      <Box
        sx={{
          textAlign: "center",
          px: isMobile ? 0.5 : 1,
          mb: isMobile ? 1.4 : 1.8,
        }}
      >
        <Typography
          sx={{
            fontSize: "24px",
            fontWeight: 700,
            lineHeight: 1,
            color: "#F3F8FF",
            letterSpacing: 0.3,
            mb: 0.6,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              fontSize: "19px",
              fontWeight: 600,
              color: "#EAF1FF",
              lineHeight: 1.2,
            }}
          >
            {subtitle}
          </Typography>
        )}
        {description && (
          <Typography
            sx={{
              fontSize: "12px",
              color: "rgba(203,218,243,0.75)",
              mt: 0.8,
              letterSpacing: 0.2,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>

      <Divider sx={{ backgroundColor: "rgba(180,201,236,0.25)", marginBottom: "6px" }} />

      <List sx={{ padding: 0 }}>
        {menuItems.map((item, index) => {
          const isActive = selectedView === item.view;

          return (
            <ListItem
              key={index}
              onClick={() => handleClick(item.view, item.action)}
              sx={{
                margin: isMobile ? "6px 0" : "8px 0",
                padding: isMobile ? "10px 10px" : "11px 12px",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                position: "relative",
                cursor: "pointer",
                backgroundColor: isActive ? "rgba(0, 201, 214, 0.2)" : "transparent",
                border: isActive
                  ? "1px solid rgba(0, 227, 255, 0.32)"
                  : "1px solid rgba(255,255,255,0.06)",
                color: isActive ? "#DFFAFF" : "#E7EEF9",
                transition: "all 0.25s ease",
                "&:hover": {
                  backgroundColor: "rgba(136, 174, 234, 0.12)",
                  borderColor: "rgba(165, 203, 255, 0.35)",
                  transform: "translateX(2px)",
                },
              }}
            >
              <Box
                sx={{
                  marginRight: isMobile ? "8px" : "10px",
                  fontSize: "22px",
                  display: "flex",
                  justifyContent: "center",
                  color: "inherit",
                  width: 28,
                  height: 28,
                  alignItems: "center",
                  borderRadius: "8px",
                  backgroundColor: isActive
                    ? "rgba(0, 227, 255, 0.15)"
                    : "rgba(255,255,255,0.05)",
                }}
              >
                {item.icon}
              </Box>

              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  sx: {
                    fontSize: isMobile ? "15px" : "16px",
                    fontWeight: isActive ? 700 : 500,
                    letterSpacing: 0.15,
                  },
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default SideMenu;
